import { Injectable, inject, resource, computed, signal, untracked, DestroyRef } from "@angular/core";
import { isiFirmTax, productStringFromApplicationType } from "@app/views-interfaces";
import { IframeMessagesService } from "@shared/iframe-messages-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import { DashboardsService } from "@shared/dashboards-service";
import { AuthService } from "@shared/auth";

interface UserAvailableDocument {
    dashboardId: string;
    dashboardCardIds: string[];
}

interface AvailableDocumentsResource {
    applicationType: number | undefined;
    taxYear: number | null | undefined;
}

type DashboardIdAvailableDocumentMap = Map<string, Set<string>>;
type DashboardYearAvailableDocumentMap = Map<number, DashboardIdAvailableDocumentMap>;
type DashboardTypeAvailableDocumentMap = Map<string, DashboardYearAvailableDocumentMap>;

@Injectable({ providedIn: "root" })
export class IfirmTaxCardsService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    private isReloading = true;
    private refreshTimer = setInterval(() => this.reload(), 3600000);

    constructor() {
        this.destroyRef.onDestroy(() => clearInterval(this.refreshTimer));

        this.authService.sessionChangedReceivedEvent$.subscribe(async () => {
            this.reload();
        });
    }

    private reload() {
        this.isReloading = !this.authService.inReloginPrompted;
        this.availableDocumentsResource.reload();
    }

    private availableDocumentsResource = resource<DashboardIdAvailableDocumentMap | undefined, AvailableDocumentsResource>({
        params: () => ({
            applicationType: this.dashboardsService.currentDashboardType(),
            taxYear: this.dashboardsService.currentDashboardYear(),
        }),
        loader: async ({ params }) => {
            const { applicationType, taxYear } = params;
            if (!applicationType || !taxYear || !isiFirmTax(applicationType) || this.authService.inReloginPrompted) return;

            const productType = productStringFromApplicationType(applicationType);
            if (!this.isReloading && this.availableDocuments().get(productType)?.get(taxYear)) {
                return this.availableDocuments().get(productType)?.get(taxYear);
            }

            this.isReloading = false;
            const documents = await this.getUserAvailableDocuments(productType, taxYear);
            if (documents?.length) {
                const dashboardYearMap: DashboardIdAvailableDocumentMap = new Map();
                for (const item of documents) {
                    dashboardYearMap.set(item.dashboardId, new Set(item.dashboardCardIds));
                }
                this.availableDocuments.update((availableDocuments) => {
                    const updated = new Map(availableDocuments);
                    const dashboardTypeMap: DashboardYearAvailableDocumentMap = updated.get(productType) ?? new Map();
                    dashboardTypeMap.set(taxYear, dashboardYearMap);
                    updated.set(productType, dashboardTypeMap);
                    return updated;
                });
                return dashboardYearMap;
            }

            return;
        },
    });

    private availableDocuments = signal<DashboardTypeAvailableDocumentMap>(new Map());

    public currentAvailableDocuments = computed(
        () => {
            const availableDocuments = this.availableDocumentsResource.value();
            const dashboardId = this.dashboardsService.sDashboardId();
            if (!dashboardId || !availableDocuments || !availableDocuments.size) return;

            return availableDocuments.get(dashboardId);
        },
        { equal: (a, b) => this.availableDocumentsEqual(a, b) },
    );

    private availableDocumentsEqual(a: Set<string> | undefined, b: Set<string> | undefined): boolean {
        if (a === b) return true;

        if (!a || !b) return false;

        if (a.size !== b.size) return false;

        for (const item of a) {
            if (!b.has(item)) return false;
        }

        return true;
    }

    public async deleteCards(dashboardId: string, cards: string[]) {
        await this.iframeMessagesService.postMessageResp("deleteCards", { dashboardId, cards }, "cardsDeleted");
    }

    private async getUserAvailableDocuments(productType: string, taxYear: number): Promise<UserAvailableDocument[] | undefined> {
        try {
            const result = await this.iframeMessagesService.postMessageResp(
                "getAvailableDocuments",
                { productType, taxYear },
                "gotiFirmTaxAvailableDocuments",
            );
            if (result.status === "success") return result.response as UserAvailableDocument[];
            else {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "defaultError",
                });
            }
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
        return;
    }

    public isCardAvailable(cardId: string): boolean {
        if (!this.dashboardsService.currentDashboardIsFirmTax()) return true;
        return this.currentAvailableDocuments()?.has(cardId) ?? false;
    }

    // Avoid using it all the times because can create problems. Only to be used in a route guard
    public async waitForDocuments(): Promise<void> {
        while (this.availableDocumentsResource.isLoading()) {
            await new Promise((resolve) => setTimeout(resolve, 10));
        }
    }
}
